import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  FolderUp, Plus, Loader2, Download, Archive, Trash2, AlertTriangle,
  StickyNote, Pencil, X, Save, Eye, Settings2, Check, FileText,
} from "lucide-react";
import { ViewCountBadge } from "@/components/ViewCountBadge";

const CRIMSON = "#bf3b4f";
const SOURCES = ["ERP Next (Formerly My Prydes Support)", "Shift Care", "Flow Logic", "Good Human", "Other"];

// File extensions that browsers can display natively in an <iframe> or <img>
const VIEWABLE_PDF = ["pdf"];
const VIEWABLE_IMG = ["jpg", "jpeg", "png", "gif", "webp", "svg"];

function getExt(filename) {
  if (!filename) return "";
  return filename.split(".").pop().toLowerCase();
}

function isViewable(filename) {
  const ext = getExt(filename);
  return VIEWABLE_PDF.includes(ext) || VIEWABLE_IMG.includes(ext);
}

async function fileToDataUrl(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
}

// ------------------------------------------------------------------ document preview modal ---
function DocPreviewModal({ doc, onClose, onDownload }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const ext = getExt(doc.office_file_name);
  const isImg = VIEWABLE_IMG.includes(ext);
  const isPdf = VIEWABLE_PDF.includes(ext);
  const canView = isImg || isPdf;

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    let url = null;
    api.externalDocumentFile(doc.id)
      .then((blob) => { url = URL.createObjectURL(blob); setObjectUrl(url); })
      .catch(() => setErr("Could not load the document."))
      .finally(() => setLoading(false));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [doc.id, canView]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full p-0 gap-0 overflow-hidden flex flex-col"
        style={{ height: "85vh" }} data-testid={`ext-preview-modal-${doc.id}`}>
        <DialogHeader className="flex-none px-4 pt-4 pb-3 border-b border-slate-100">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-sm font-semibold text-slate-800 truncate leading-tight">{doc.ext_type_name}</DialogTitle>
              {doc.office_file_name && <p className="text-[11px] text-slate-400 mt-0.5 truncate">{doc.office_file_name}</p>}
            </div>
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 bg-slate-50 flex items-center justify-center overflow-auto">
          {loading && <div className="flex flex-col items-center gap-2 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /><span className="text-xs">Loading document…</span></div>}
          {!loading && err && (
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <FileText className="h-10 w-10 text-slate-300" />
              <p className="text-sm text-slate-500">{err}</p>
              <Button size="sm" variant="outline" onClick={() => onDownload(doc)} className="gap-1"><Download className="h-3.5 w-3.5" />Download instead</Button>
            </div>
          )}
          {!loading && !err && !canView && (
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <FileText className="h-10 w-10 text-slate-300" />
              <p className="text-sm font-medium text-slate-600">Preview not available</p>
              <p className="text-xs text-slate-400">.{ext || "unknown"} files cannot be previewed in the browser.</p>
              <Button size="sm" variant="outline" onClick={() => onDownload(doc)} className="gap-1 mt-1"><Download className="h-3.5 w-3.5" />Download to open</Button>
            </div>
          )}
          {!loading && !err && objectUrl && isPdf && (
            <iframe src={objectUrl} title={doc.ext_type_name} className="w-full h-full border-0" data-testid={`ext-preview-iframe-${doc.id}`} />
          )}
          {!loading && !err && objectUrl && isImg && (
            <div className="p-4 flex items-center justify-center w-full h-full overflow-auto">
              <img src={objectUrl} alt={doc.ext_type_name} className="max-w-full max-h-full object-contain rounded shadow" data-testid={`ext-preview-img-${doc.id}`} />
            </div>
          )}
        </div>
        <DialogFooter className="flex-none px-4 py-3 border-t border-slate-100 bg-white">
          <Button variant="outline" onClick={onClose} className="h-8 text-xs">Close</Button>
          <Button onClick={() => onDownload(doc)} className="h-8 text-xs gap-1" style={{ backgroundColor: CRIMSON }}>
            <Download className="h-3.5 w-3.5" />Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ inline metadata edit panel ---
function DocEditPanel({ doc, onSaved, onClose }) {
  const [draft, setDraft] = useState({
    source_system: doc.source_system || "",
    start_date: doc.start_date || "",
    end_date: doc.end_date || "",
    visible_to_workers: doc.visible_to_workers,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.patchExternalDocument(doc.id, {
        source_system: draft.source_system || null,
        start_date: draft.start_date || null,
        end_date: draft.end_date || null,
        visible_to_workers: draft.visible_to_workers,
      });
      onSaved(updated);
      toast.success("Document updated.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save changes.");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-2 rounded-md border border-sky-200 bg-sky-50 p-3 space-y-3"
      data-testid={`ext-doc-edit-panel-${doc.id}`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1.5">
        <Settings2 className="h-3.5 w-3.5 text-sky-600 shrink-0" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-sky-700">Edit document settings</span>
      </div>
      <div>
        <Label className="text-[11px] font-semibold text-slate-600">Source system</Label>
        <Select value={draft.source_system || "none"}
          onValueChange={(v) => setDraft((f) => ({ ...f, source_system: v === "none" ? "" : v }))}>
          <SelectTrigger className="h-8 text-xs mt-1 bg-white" data-testid={`ext-doc-edit-source-${doc.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none" className="text-sm text-slate-400">—</SelectItem>
            {SOURCES.map((s) => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px] font-semibold text-slate-600">Start date</Label>
          <Input type="date" value={draft.start_date} onChange={(e) => setDraft((f) => ({ ...f, start_date: e.target.value }))}
            className="h-8 text-xs mt-1 bg-white" data-testid={`ext-doc-edit-start-${doc.id}`} />
        </div>
        <div>
          <Label className="text-[11px] font-semibold text-slate-600">End / expiry date</Label>
          <Input type="date" value={draft.end_date} onChange={(e) => setDraft((f) => ({ ...f, end_date: e.target.value }))}
            className="h-8 text-xs mt-1 bg-white" data-testid={`ext-doc-edit-end-${doc.id}`} />
        </div>
      </div>
      <div className="flex items-center justify-between rounded-lg bg-white border border-slate-200 px-3 py-2.5">
        <div className="min-w-0 pr-4">
          <span className="text-xs font-medium text-slate-700">Visible to support workers</span>
          <p className="text-[10px] text-slate-400 mt-0.5 leading-snug">
            When enabled, workers see this document in their app. Pages are rendered as
            watermarked images — the original file is never exposed to workers.
          </p>
        </div>
        <Switch checked={draft.visible_to_workers}
          onCheckedChange={(v) => setDraft((f) => ({ ...f, visible_to_workers: v }))}
          data-testid={`ext-doc-edit-visible-${doc.id}`} />
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button size="sm" variant="ghost" onClick={onClose}
          className="h-7 text-xs px-2 text-slate-500 hover:text-slate-700" data-testid={`ext-doc-edit-cancel-${doc.id}`}>
          <X className="h-3.5 w-3.5 mr-1" />Cancel
        </Button>
        <Button size="sm" onClick={save} disabled={saving}
          className="h-7 text-xs px-3 bg-sky-600 hover:bg-sky-700 text-white" data-testid={`ext-doc-edit-save-${doc.id}`}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}Save changes
        </Button>
      </div>
    </div>
  );
}


function DocNoteEditor({ doc, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(doc.notes || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(doc.notes || "");
  }, [doc.notes, editing]);

  const openEdit = (e) => { e.stopPropagation(); setDraft(doc.notes || ""); setEditing(true); };
  const cancel = (e) => { e?.stopPropagation(); setDraft(doc.notes || ""); setEditing(false); };

  const save = async (e) => {
    e.stopPropagation();
    setSaving(true);
    try {
      const updated = await api.updateExternalDocumentNotes(doc.id, draft.trim() || null);
      onSaved(updated);
      setEditing(false);
      toast.success("Note saved.");
    } catch { toast.error("Could not save note."); }
    finally { setSaving(false); }
  };

  const hasNote = !!(doc.notes || "").trim();

  if (editing) {
    return (
      <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2.5" onClick={(e) => e.stopPropagation()} data-testid={`ext-doc-note-editor-${doc.id}`}>
        <div className="flex items-center gap-1.5 mb-1.5">
          <StickyNote className="h-3.5 w-3.5 text-amber-600 shrink-0" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Office note</span>
          <span className="text-[10px] text-amber-500 ml-1">· internal only, not visible to workers</span>
        </div>
        <Textarea autoFocus rows={3} value={draft} onChange={(e) => setDraft(e.target.value)}
          placeholder="Add an internal note about this document…"
          className="text-xs min-h-[64px] resize-none bg-white border-amber-200 focus:border-amber-400 focus:ring-amber-200"
          data-testid={`ext-doc-note-textarea-${doc.id}`} />
        <div className="flex items-center justify-end gap-2 mt-2">
          <Button size="sm" variant="ghost" onClick={cancel} className="h-7 text-xs px-2 text-slate-500 hover:text-slate-700" data-testid={`ext-doc-note-cancel-${doc.id}`}>
            <X className="h-3.5 w-3.5 mr-1" />Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="h-7 text-xs px-3 bg-amber-600 hover:bg-amber-700 text-white" data-testid={`ext-doc-note-save-${doc.id}`}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}Save note
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2" data-testid={`ext-doc-note-${doc.id}`}>
      {hasNote ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2">
          <StickyNote className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-600">Office note</span>
            </div>
            <p className="text-xs text-slate-700 whitespace-pre-wrap leading-snug" data-testid={`ext-doc-note-text-${doc.id}`}>{doc.notes}</p>
          </div>
          {canEdit && (
            <button onClick={openEdit} title="Edit note" className="text-amber-400 hover:text-amber-700 p-0.5 shrink-0 transition-colors" data-testid={`ext-doc-note-edit-${doc.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ) : (
        canEdit && (
          <button onClick={openEdit} className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-amber-600 transition-colors py-0.5" data-testid={`ext-doc-note-add-${doc.id}`}>
            <StickyNote className="h-3 w-3" /><span>Add office note</span>
          </button>
        )
      )}
    </div>
  );
}


export function ExternalDocumentsTab({ participant, onChanged }) {
  const { can } = useAuth();
  const [docs, setDocs] = useState(null);
  const [types, setTypes] = useState([]);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ext_type_id: "", source_system: "", start_date: "", end_date: "", visible_to_workers: false });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  // Preview modal: stores the doc object currently being previewed
  const [preview, setPreview] = useState(null);
  // Inline edit panel: stores the id of the doc whose settings are being edited
  const [editingId, setEditingId] = useState(null);

  const load = () => api.externalDocuments(participant).then(setDocs).catch(() => { setDocs([]); toast.error("Could not load external documents."); });
  useEffect(() => {
    load();
    api.externalTypes().then(setTypes).catch(() => {});
    api.participantDocumentViewStats(participant).then(setStats).catch(() => {});
    /* eslint-disable-next-line */
  }, [participant]);

  const activeOfType = (tid) => (docs || []).find((d) => d.ext_type_id === tid && d.workflow_state === "Active");

  // Update a single doc in local state — avoids a full reload after saves.
  const handleDocSaved = (updatedDoc) => {
    setDocs((prev) => (prev || []).map((d) => d.id === updatedDoc.id ? updatedDoc : d));
  };

  const doUpload = async () => {
    if (!form.ext_type_id) return toast.error("Choose a document type.");
    if (!file) return toast.error("Choose a file to upload.");
    const prior = activeOfType(form.ext_type_id);
    if (prior && !window.confirm(`There is already an Active ${prior.ext_type_name}. Uploading a new one will archive the current one. Continue?`)) return;
    setBusy(true);
    try {
      const data = await fileToDataUrl(file);
      await api.uploadExternalDocument({ participant, ...form, filename: file.name, data });
      toast.success("Document uploaded.");
      setOpen(false); setFile(null);
      setForm({ ext_type_id: "", source_system: "", start_date: "", end_date: "", visible_to_workers: false });
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed."); }
    finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      const blob = await api.externalDocumentFile(d.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = d.office_file_name || "document"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed."); }
  };
  const archive = async (d) => { try { await api.archiveExternalDocument(d.id); toast.success("Archived."); load(); onChanged?.(); } catch { toast.error("Failed."); } };
  const del = async (d) => { if (!window.confirm("Delete this document permanently?")) return; try { await api.deleteExternalDocument(d.id); toast.success("Deleted."); load(); onChanged?.(); } catch (e) { toast.error(e.response?.data?.detail || "Failed."); } };

  return (
    <div className="mt-4 space-y-3" data-testid="external-docs-tab">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><FolderUp className="h-3.5 w-3.5" /> External documents</div>
        {can.has("participant_docs.edit") && <Button size="sm" onClick={() => setOpen(true)} style={{ backgroundColor: CRIMSON }} data-testid="new-external-doc"><Plus className="h-4 w-4 mr-1" />Upload document</Button>}
      </div>
      {docs === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
        <div className="space-y-2">
          {docs.length === 0 && <div className="text-xs text-slate-400 py-4">No external documents uploaded yet.</div>}
          {docs.map((d) => (
            <div key={d.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`external-doc-${d.id}`}>
              {/* ── Document metadata row ── */}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  {/* Clickable title → opens preview modal */}
                  <button
                    className="text-sm font-medium text-slate-800 flex items-center gap-2 text-left hover:text-sky-700 transition-colors group"
                    onClick={() => setPreview(d)}
                    data-testid={`ext-doc-title-${d.id}`}
                  >
                    <span className="truncate">{d.ext_type_name}</span>
                    <Eye className="h-3.5 w-3.5 text-slate-300 group-hover:text-sky-500 shrink-0 transition-colors" />
                    {d.visible_to_workers
                      ? <span className="text-[10px] text-emerald-600 font-medium bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">Visible to workers</span>
                      : <span className="text-[10px] text-slate-400 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">Office only</span>}
                  </button>
                  <div className="text-[10px] text-slate-400 flex flex-wrap gap-x-3 items-center mt-0.5">
                    {d.source_system && <span>Source: {d.source_system}</span>}
                    <span>Period: {d.start_date || "—"} → {d.end_date || "—"}</span>
                    <ViewCountBadge stat={stats[`external:${d.id}`]} />
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {d.expired && d.workflow_state === "Active" && <Badge className="text-[9px] py-0 bg-red-50 text-red-600 border-red-200">Expired</Badge>}
                  <Badge className={`text-[9px] py-0 ${d.workflow_state === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-400 border-slate-200"}`}>{d.workflow_state}</Badge>
                  {/* View (preview) button */}
                  <button onClick={() => setPreview(d)} className="text-slate-400 hover:text-sky-600 p-1 transition-colors" title="Preview document" data-testid={`ext-view-${d.id}`}>
                    <Eye className="h-4 w-4" />
                  </button>
                  {/* Download button */}
                  <button onClick={() => download(d)} className="text-slate-400 hover:text-slate-700 p-1 transition-colors" title="Download" data-testid={`ext-download-${d.id}`}><Download className="h-4 w-4" /></button>
                  {/* Edit settings — CAN_EDIT only, toggles the inline edit panel */}
                  {can.has("participant_docs.edit") && (
                    <button
                      onClick={() => setEditingId(editingId === d.id ? null : d.id)}
                      className={`p-1 transition-colors ${editingId === d.id ? "text-sky-600" : "text-slate-400 hover:text-sky-600"}`}
                      title="Edit document settings"
                      data-testid={`ext-edit-settings-${d.id}`}
                    >
                      <Settings2 className="h-4 w-4" />
                    </button>
                  )}
                  {can.has("participant_docs.edit") && d.workflow_state === "Active" && <button onClick={() => archive(d)} className="text-slate-400 hover:text-amber-600 p-1 transition-colors" title="Archive" data-testid={`ext-archive-${d.id}`}><Archive className="h-4 w-4" /></button>}
                  {can.has("participant_docs.delete_signed") && <button onClick={() => del(d)} className="text-slate-300 hover:text-rose-600 p-1 transition-colors" title="Delete (admin)" data-testid={`ext-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
              {/* ── Inline edit panel (worker visibility, dates, source system) ── */}
              {can.has("participant_docs.edit") && editingId === d.id && (
                <DocEditPanel
                  doc={d}
                  onSaved={(updated) => { handleDocSaved(updated); onChanged?.(); }}
                  onClose={() => setEditingId(null)}
                />
              )}
              {/* ── Office notes ── */}
              <DocNoteEditor doc={d} canEdit={can.has("participant_docs.edit")} onSaved={handleDocSaved} />
            </div>
          ))}
        </div>
      )}

      {/* ── Document preview modal ── */}
      {preview && (
        <DocPreviewModal
          doc={preview}
          onClose={() => setPreview(null)}
          onDownload={(d) => download(d)}
        />
      )}

      {/* ── Upload dialog ── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="external-upload-dialog">
          <DialogHeader><DialogTitle className="text-base font-brand">Upload external document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Document type</Label>
              <Select value={form.ext_type_id} onValueChange={(v) => setForm((f) => ({ ...f, ext_type_id: v }))}>
                <SelectTrigger className="h-9 text-sm mt-1" data-testid="ext-upload-type"><SelectValue placeholder="Choose a type…" /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t.id} value={t.id} className="text-sm">{t.name}</SelectItem>)}
                  {types.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No external types. Add them in Settings → Document Compliance.</div>}
                </SelectContent>
              </Select>
              {form.ext_type_id && activeOfType(form.ext_type_id) && (
                <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />An Active document of this type exists — it will be archived on upload.</p>
              )}
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Created in (source system, optional)</Label>
              <Select value={form.source_system || "none"} onValueChange={(v) => setForm((f) => ({ ...f, source_system: v === "none" ? "" : v }))}>
                <SelectTrigger className="h-9 text-sm mt-1" data-testid="ext-upload-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="text-sm text-slate-400">—</SelectItem>
                  {SOURCES.map((s) => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[11px] font-semibold text-slate-600">Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} className="h-9 text-sm mt-1" data-testid="ext-upload-start" /></div>
              <div><Label className="text-[11px] font-semibold text-slate-600">End / expiry date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} className="h-9 text-sm mt-1" data-testid="ext-upload-end" /></div>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
              <div>
                <span className="text-xs font-medium text-slate-700">Visible to support workers</span>
                <p className="text-[10px] text-slate-400 mt-0.5 leading-snug">Workers see this document in their app. Pages are rendered as watermarked images — the original file is never exposed to workers.</p>
              </div>
              <Switch checked={form.visible_to_workers} onCheckedChange={(v) => setForm((f) => ({ ...f, visible_to_workers: v }))} data-testid="ext-upload-visible" />
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">File</Label>
              <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-9 text-sm mt-1" data-testid="ext-upload-file" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={doUpload} disabled={busy} style={{ backgroundColor: CRIMSON }} data-testid="ext-upload-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FolderUp className="h-4 w-4 mr-1.5" />Upload</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
